import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { siteInductionsService } from '../../../services/siteInductionsService';

const InductionCard = ({ assignment, onCompleted }) => {
  const induction = assignment?.induction;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [completed, setCompleted] = useState(assignment?.completed || false);
  const [completedAt, setCompletedAt] = useState(assignment?.completed_at || null);
  const [viewLoading, setViewLoading] = useState(false);

  const formatDate = (ts) => {
    if (!ts) return '';
    return new Date(ts)?.toLocaleString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  const handleViewDocument = async () => {
    setError('');
    // Defensive guard mirroring RamsCard from [gwwbt45U]: if the induction
    // embed didn't carry a file_path (RLS denial, deleted record, schema
    // surprise), fail loudly with an actionable message instead of calling
    // getSignedUrl(undefined).
    if (!induction?.file_path) {
      console.error('[InductionCard] view document: induction embed missing', { assignmentId: assignment?.id, inductionId: assignment?.induction_id });
      setError("This induction's details are unavailable. Please contact your site admin — the assignment may reference a deleted document.");
      return;
    }

    // Open the destination tab synchronously inside the click handler so
    // the user-gesture chain is preserved across the upcoming `await`.
    // Modern browsers (Safari especially) silently block `window.open` if
    // it runs after an async hop. Same fix as RamsCard from [gwwbt45U].
    const newWin = window?.open('', '_blank', 'noopener,noreferrer');

    setViewLoading(true);
    const result = await siteInductionsService?.getSignedUrl(induction?.file_path, induction?.title);
    setViewLoading(false);

    if (result?.url) {
      if (newWin) {
        newWin.location.href = result.url;
      } else {
        // Even the synchronous open was blocked. Fall back to in-place
        // navigation — the signed URL is download-attachment, so the
        // browser handles it as a download without leaving the SPA.
        window.location.href = result.url;
      }
      return;
    }

    if (newWin) newWin.close();

    const err = result?.error;
    console.error('[InductionCard] view document failed:', { filePath: induction?.file_path, code: err?.code, message: err?.message });
    if (err?.code === 'PGRST301') {
      setError(`Could not open document — permission denied: ${err?.message}`);
    } else {
      setError(`Could not open document: ${err?.message ?? 'unknown error'}`);
    }
  };

  const handleComplete = async () => {
    setLoading(true);
    setError('');
    const result = await siteInductionsService?.completeInduction(induction?.id, assignment?.worker_user_id);
    setLoading(false);
    if (result?.error) {
      const err = result?.error;
      console.error('[InductionCard] complete failed:', { inductionId: induction?.id, code: err?.code, message: err?.message });
      if (err?.code === 'PGRST301' || err?.code === '42501') {
        setError(`Failed to complete — permission denied: ${err?.message}`);
      } else {
        setError(`Failed to complete: ${err?.message ?? 'unknown error'}`);
      }
      return;
    }
    const ts = result?.data?.completed_at || new Date()?.toISOString();
    setCompleted(true);
    setCompletedAt(ts);
    onCompleted?.(induction?.id);
  };

  return (
    <div className={`bg-card border rounded-xl p-5 transition-all ${
      completed ? 'border-success/30' : 'border-amber-300'
    }`}>
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
            completed ? 'bg-success/10' : 'bg-amber-50'
          }`}>
            <Icon
              name={completed ? 'GraduationCap' : 'BookOpen'}
              size={20}
              className={completed ? 'text-success' : 'text-amber-600'}
            />
          </div>
          <div>
            <h3 className="font-semibold text-foreground text-base leading-tight">{induction?.title}</h3>
            <div className="flex flex-wrap items-center gap-2 mt-1">
              {induction?.site?.site_name && (
                <span className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Icon name="MapPin" size={11} />
                  {induction?.site?.site_name}
                </span>
              )}
              <span className="flex items-center gap-1 text-xs text-muted-foreground">
                <Icon name="Calendar" size={11} />
                Uploaded {formatDate(induction?.created_at)?.split(',')?.[0]}
              </span>
            </div>
          </div>
        </div>

        {/* Status Badge */}
        {completed ? (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-success/10 text-success shrink-0">
            <Icon name="CheckCircle" size={12} />
            Completed
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-700 shrink-0">
            <Icon name="Clock" size={12} />
            Pending
          </span>
        )}
      </div>

      {/* Completed timestamp */}
      {completed && completedAt && (
        <div className="flex items-center gap-2 bg-success/5 border border-success/20 rounded-lg px-3 py-2 mb-4">
          <Icon name="CheckCircle" size={14} className="text-success shrink-0" />
          <p className="text-xs text-success font-medium">
            You completed this induction on {formatDate(completedAt)}
          </p>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 bg-destructive/10 border border-destructive/20 rounded-lg px-3 py-2 mb-4">
          <Icon name="AlertCircle" size={14} className="text-destructive shrink-0" />
          <p className="text-xs text-destructive">{error}</p>
        </div>
      )}

      {/* Actions */}
      <div className="flex flex-col sm:flex-row gap-2">
        <Button
          variant="outline"
          onClick={handleViewDocument}
          disabled={viewLoading}
          className="flex-1 h-10"
        >
          {viewLoading ? (
            <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-accent mr-2" />Opening...</>
          ) : (
            <><Icon name="ExternalLink" size={15} className="mr-2" />View Document</>
          )}
        </Button>

        {!completed && (
          <Button
            onClick={handleComplete}
            disabled={loading}
            className="flex-1 h-10 bg-success hover:bg-success/90 text-white"
          >
            {loading ? (
              <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />Completing...</>
            ) : (
              <><Icon name="CheckCircle" size={15} className="mr-2" />Complete Induction</>
            )}
          </Button>
        )}
      </div>
    </div>
  );
};

export default InductionCard;
